import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { competences, enquetes, stats2024, meteo, articleMoment, parcours } from '../mock';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ArrowRight, ArrowUpRight, MapPin, CloudSun, Clapperboard, BookOpen } from 'lucide-react';

export function CompetencesSection() {
  const { openRegister } = useAuth();
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#0f0f10] to-[#080808] overflow-hidden">
        <div className="grid lg:grid-cols-[1fr_1.2fr]">
          <div className="p-5 sm:p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/5">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Pédagogique · REMC officiel</div>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[34px] lg:text-[44px] leading-[1.05] sm:leading-[1.02] tracking-tight">Mon Livret d’Apprentissage</h2>
            <p className="text-white/60 text-[13.5px] sm:text-[14.5px] mt-3 sm:mt-4 max-w-md">Le référentiel officiel des 38 compétences à maîtriser pour le permis B. Suis ta progression et auto-évalue-toi gratuitement.</p>
            <div className="mt-5 sm:mt-7 flex flex-wrap gap-2 sm:gap-3">
              <Link
                to="/livret"
                data-testid="home-livret-cta"
                className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] sm:text-[14px] font-semibold inline-flex items-center gap-2 shadow-lg shadow-red-900/40"
              >
                Découvrir mon livret <ArrowRight size={15} />
              </Link>
              <button
                type="button"
                data-testid="home-livret-register"
                onClick={openRegister}
                className="h-11 px-5 rounded-full border border-white/15 text-white text-[13px] sm:text-[14px] font-medium hover:bg-white/5"
              >
                Créer un compte gratuit
              </button>
            </div>
            <div className="mt-4 sm:mt-6 text-[11px] sm:text-[12px] text-white/40 font-mono-spec">100 % gratuit · sauvegardé sur ton compte</div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8 grid grid-cols-2 gap-2.5 sm:gap-3.5">
            {competences.map((c, i) => (
              <div key={i} className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#141416] p-3.5 sm:p-5 hover:border-red-700/40 transition group">
                <div className="font-mono-spec text-red-500/80 text-[12px] sm:text-[13px] group-hover:text-red-500">{c.code}</div>
                <h4 className="font-serif-editorial text-white text-[16px] sm:text-[22px] leading-tight mt-1">{c.title}</h4>
                <p className="text-white/55 text-[11.5px] sm:text-[13px] mt-1">{c.subtitle}</p>
                <div className="mt-3 sm:mt-4 text-[10.5px] sm:text-[11px] text-white/40 font-mono-spec">{c.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function EnquetesSection() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.listArticles({ type: 'enquete', limit: 5 })
      .then((d) => setItems(d.length > 0 ? d : null))
      .catch(() => setItems(null));
  }, []);

  // Build the same shape as the mock or use fallback
  const data = items
    ? items.map((a, i) => ({
        n: a.issue_number || `N°${i + 1}`,
        tag: i === 0 ? 'À LA UNE' : '',
        category: a.theme ? `Enquête · ${a.theme}` : '',
        title: a.title,
        desc: a.subtitle || '',
        meta: `${a.read_time_min} min de lecture`,
        img: a.cover_image,
        slug: a.slug,
      }))
    : enquetes;

  const main = data[0];
  const rest = data.slice(1, 4);

  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="mb-5 sm:mb-8">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Série complète · publiée régulièrement</div>
        <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-[1.02] sm:leading-[0.95] tracking-tight">Enquêtes & Témoignages</h2>
        <p className="mt-3 sm:mt-4 text-white/60 text-[13.5px] sm:text-[15px] max-w-3xl">Accidentologie chiffrée, témoignages, sources officielles (ONISR, Sécurité Routière, ASSURPRO). Pour comprendre ce qui tue réellement en voiture.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4">
        <Link to={main.slug ? `/magazine/${main.slug}` : '#'} className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#111113] aspect-[16/11] card-lift">
          <img src={main.img} alt={main.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
          <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex items-center gap-2">
            {main.tag && <span className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">{main.tag}</span>}
            <span className="text-white/70 text-[10.5px] sm:text-[11px] font-mono-spec">{main.n}</span>
          </div>
          <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
            {main.category && <div className="text-red-400 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold mb-1.5 sm:mb-2">{main.category}</div>}
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] lg:text-[32px] leading-tight">{main.title}</h3>
            {main.desc && <p className="text-white/70 text-[12.5px] sm:text-[14px] mt-2 sm:mt-3 max-w-xl line-clamp-2 sm:line-clamp-3">{main.desc}</p>}
            <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-white/80 text-[11.5px] sm:text-[12.5px] font-mono-spec">
              <span>{main.meta}</span><span>·</span>
              <span className="text-red-400 font-semibold">Lire l’enquête →</span>
            </div>
          </div>
        </Link>
        <div className="grid gap-3 sm:gap-4 content-start">
          {rest.map((e, i) => (
            <Link key={i} to={e.slug ? `/magazine/${e.slug}` : '#'} className="group flex gap-3 sm:gap-4 rounded-xl sm:rounded-2xl border border-white/10 bg-[#111113] p-3 sm:p-4 hover:border-red-700/40 transition">
              <img src={e.img} alt={e.title} className="w-24 h-24 sm:w-36 sm:h-32 object-cover rounded-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white/50 text-[10.5px] sm:text-[11px] font-mono-spec">{e.n}</div>
                <h4 className="font-serif-editorial text-white text-[15px] sm:text-[18px] lg:text-[20px] leading-tight mt-1 line-clamp-3">{e.title}</h4>
                <div className="mt-2 sm:mt-3 inline-flex items-center gap-2 text-white/60 text-[11px] sm:text-[12px] font-mono-spec">
                  <span>{e.meta}</span><span>·</span>
                  <span className="text-red-400 font-semibold">Lire →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div className="mt-5 sm:mt-6 text-center">
        <Link to="/magazine" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-white text-[12.5px] sm:text-[13.5px] font-medium hover:bg-white/5">Voir toutes les enquêtes <ArrowRight size={14} /></Link>
        <div className="text-[11px] sm:text-[12px] text-white/40 font-mono-spec mt-3">100 % gratuit · Sans inscription · Sources officielles citées</div>
      </div>
    </section>
  );
}

export function SideInfoSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
        {/* Stats card */}
        <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-red-950/30 to-[#0d0d0e] p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2 py-0.5 rounded-md bg-white/5 text-white/70 text-[10px] font-bold uppercase tracking-wider border border-white/10">100 % officiel</span>
          </div>
          <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight">Vidéos & simulateur officiels Sécurité routière</h3>
          <p className="text-white/60 text-[12px] sm:text-[13px] mt-2">Simulateur #EquipezVous + 4 campagnes pour renforcer ton apprentissage.</p>
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-5 border-t border-white/5">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-red-400 font-semibold">Sécurité routière · 2024</div>
            <div className="text-[10.5px] sm:text-[11px] text-white/40 font-mono-spec mt-0.5">{stats2024.source}</div>
            <div className="mt-2 sm:mt-3 text-white font-serif-editorial text-[20px] sm:text-[24px] leading-tight">{stats2024.primary}</div>
            <p className="text-white/65 text-[12px] sm:text-[13px] mt-2" dangerouslySetInnerHTML={{__html: stats2024.secondary.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')}} />
            <p className="text-white/50 text-[11.5px] sm:text-[12px] mt-3">{stats2024.caption}</p>
          </div>
        </div>
        {/* Meteo + F1 */}
        <div className="space-y-3 sm:space-y-4">
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2 text-white"><CloudSun className="text-red-400" size={18} /><span className="font-serif-editorial text-[17px] sm:text-[20px]">{meteo.title}</span></div>
            </div>
            <div className="flex items-center gap-2 text-white/60 text-[12px] sm:text-[13px]"><MapPin size={14} /> {meteo.location}</div>
            <div className="mt-2 sm:mt-3 text-white/40 text-[11px] sm:text-[12px] font-mono-spec">{meteo.status}</div>
            <div className="mt-4 sm:mt-5 grid grid-cols-4 gap-2">
              {['LUN', 'MAR', 'MER', 'JEU'].map((d, i) => (
                <div key={i} className="rounded-lg bg-white/5 border border-white/10 p-2 text-center">
                  <div className="text-[9.5px] sm:text-[10px] text-white/40 font-mono-spec">{d}</div>
                  <div className="text-[13px] sm:text-[14px] text-white mt-1">{18 + i}°</div>
                </div>
              ))}
            </div>
          </div>
          <Link to="/shorts" data-testid="home-shorts-card" className="rounded-xl sm:rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a0a0a] to-[#0d0d0e] p-4 sm:p-6 block card-lift">
            <div className="flex items-center gap-2 mb-2"><Clapperboard className="text-red-400" size={18} /><span className="font-serif-editorial text-white text-[17px] sm:text-[20px]">Shorts</span></div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-white/50 font-semibold">Leçons de conduite · 30 secondes</div>
            <div className="mt-3 sm:mt-4 flex items-end gap-3">
              <span className="font-serif-editorial text-red-500 text-[34px] sm:text-[44px] leading-none">30 s</span>
              <span className="text-white/60 text-[11.5px] sm:text-[12px] font-mono-spec pb-1">1 règle clé</span>
            </div>
            <div className="mt-3 sm:mt-4 text-white/60 text-[12px] sm:text-[12.5px] leading-snug">Giratoire, stop, priorités… à swiper comme TikTok, plus les vidéos de la communauté.</div>
            <span className="mt-3 sm:mt-4 inline-flex items-center gap-1.5 text-red-400 text-[12px] sm:text-[12.5px] font-semibold">Regarder les shorts <ArrowUpRight size={13} /></span>
          </Link>
        </div>
        {/* Article du moment */}
        <Link to="/magazine" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0e] flex flex-col card-lift">
          <div className="relative aspect-[16/10]">
            <img src={articleMoment.img} alt={articleMoment.title} className="absolute inset-0 w-full h-full object-cover img-zoom" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-red-600 text-white text-[10px] font-bold tracking-wider">{articleMoment.badge}</div>
          </div>
          <div className="p-4 sm:p-5">
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-red-400 font-semibold">{articleMoment.cat}</div>
            <div className="text-[10.5px] sm:text-[11px] text-white/40 font-mono-spec mt-0.5">{articleMoment.read}</div>
            <h3 className="font-serif-editorial text-white text-[18px] sm:text-[22px] leading-tight mt-2">{articleMoment.title}</h3>
            <p className="text-white/60 text-[12px] sm:text-[13px] mt-2" dangerouslySetInnerHTML={{__html: articleMoment.desc.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')}} />
            <div className="mt-3 sm:mt-4 inline-flex items-center gap-1.5 text-red-400 text-[12.5px] sm:text-[13px] font-semibold"><BookOpen size={13} /> {articleMoment.cta}</div>
          </div>
        </Link>
      </div>
    </section>
  );
}

export function ParcoursSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-14 sm:mt-24">
      <div className="flex items-end justify-between mb-5 sm:mb-8 gap-4">
        <div>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Parcours de formation</div>
          <h2 className="font-serif-editorial text-white text-[26px] sm:text-[36px] lg:text-[52px] leading-tight sm:leading-none tracking-tight">Trouve ton chemin vers la voiture.</h2>
        </div>
        <Link to="/eleve" className="hidden md:inline-flex items-center gap-1.5 text-white/80 hover:text-white text-[13.5px] font-medium">Voir tout <ArrowUpRight size={15} /></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
        {parcours.map((p, i) => (
          <Link key={i} to="/eleve" className="group relative rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 bg-[#111113] aspect-[3/4] card-lift">
            <img src={p.img} alt={p.label} className="absolute inset-0 w-full h-full object-cover img-zoom opacity-95" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3 px-1.5 sm:px-2 py-0.5 rounded-md bg-white/10 text-white text-[9px] sm:text-[10px] font-bold tracking-wider backdrop-blur">{p.age}</div>
            <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
              <h3 className="font-serif-editorial text-white text-[14px] sm:text-[18px] leading-tight">{p.label}</h3>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
